/* ==========================================================================
   AMP Slidecart — purchase-option filter
   --------------------------------------------------------------------------
   AMP's slide cart (#slidecarthq) renders a <select> of every selling plan a
   line's variant is allocated to. Loop publishes a lot of noise into that list
   (malformed "trial_and_" rows, the retired 6-week cadence), and mixing bulk
   prepaid cadences with regular monthly ones in a single dropdown reads as
   nonsense. This script hides the options that don't belong on a given line.

   ONE HARD RULE: this script may HIDE options. It must never change which plan
   a line is on. The cart is the shopper's; only the shopper edits it.

   That rule is not decoration — breaking it was a live bug. The previous
   version kept an allowlist of selling-plan ids and deleted every option not on
   it. Its bulk list was `[...DEV_BULK_PLANS, ...PROD_BULK_PLANS]` where
   PROD_BULK_PLANS was `[]`, so on production NO "Buy in Bulk" plan id was ever
   allowlisted. Add a 3-month prepaid bundle to the cart, open the drawer, and
   every bulk option was deleted, the line's own plan among them — at which
   point the fallback below re-selected the first surviving option (a plain
   monthly plan) and dispatched a `change` event. AMP dutifully rewrote the
   line, and a $58.05 three-month bundle silently became one $19.79 pack.

   An id allowlist could never have worked here: Loop allocates bulk plans PER
   SIZE (16 plans per product, different ids on every product), so the list
   would need thousands of entries and a redeploy per catalogue change. Options
   are classified by their LABEL instead, ids are only a hint, and anything
   unrecognised is KEPT rather than deleted — an extra row in a dropdown is a
   cosmetic problem, a rewritten cart line is a refund.

   Rendered by snippets/amp-subscription-filter.liquid on both layouts.
   ========================================================================== */

(function () {
  "use strict";

  /* ------------------------------------------------------------------
     Pure decision logic — no DOM. Unit-tested by
     tests/amp-subscription-filter.test.mjs, which extracts everything
     between the markers below and evaluates it directly, so keep this
     region free of DOM/browser references.
     ------------------------------------------------------------------ */
  // AMP-PLAN-FILTER-LOGIC-START
  // Selling-plan ids from the DEV store, kept only as classification HINTS for
  // labels the heuristics below can't read. Never treat these as an allowlist:
  // production plan ids are per-size and per-product and will never appear here.
  var HINT_BULK_PLAN_IDS = [
    "4388192481",
    "4388225249",
    "4388258017",
    "4388290785",
  ];
  var HINT_REGULAR_PLAN_IDS = [
    "4164026593",
    "4164059361",
    "4164092129",
    "4340744417",
    "4340777185",
    "4340809953",
    "1598685357",
    "1663205549",
    "1663238317",
    "2475327661",
  ];

  var includes = function (list, value) {
    for (var i = 0; i < list.length; i += 1) {
      if (list[i] === value) return true;
    }
    return false;
  };

  var isOneTimeOption = function (value, label) {
    var lower = (label || "").toLowerCase();
    return (
      lower.indexOf("one-time purchase") !== -1 ||
      lower.indexOf("one time purchase") !== -1 ||
      value === "" ||
      value === "onetime"
    );
  };

  // "3 month supply, delivered monthly" / "6 Month Prepaid, Deliver every
  // month" — Loop's bulk cadences always name the supply length, which is the
  // only stable signal across the per-size duplicates of the same offer.
  var looksBulk = function (label) {
    var lower = (label || "").toLowerCase();
    return (
      lower.indexOf("prepaid") !== -1 ||
      lower.indexOf("supply") !== -1 ||
      /\d+\s*month[s]?\s*(prepaid|supply)/i.test(label || "")
    );
  };

  // Ordinary recurring cadences. Tested AFTER looksBulk, because every bulk
  // label also carries a "deliver every month" tail.
  var looksRegular = function (label) {
    if (looksBulk(label)) return false;
    var lower = (label || "").toLowerCase();
    return (
      lower.indexOf("deliver every") !== -1 ||
      /every\s+\d*\s*month[s]?/i.test(label || "") ||
      /every\s+\d+\s*(day[s]?|week[s]?)/i.test(label || "")
    );
  };

  // The interval a regular cadence describes, in days, or 0 when the label
  // doesn't state one.
  //
  // This exists because Loop attaches SEVERAL subscription groups to the same
  // product, and each spells the same rhythm its own way. A Small/Medium line
  // listed ten options where six were the same four rhythms said differently:
  //   "Deliver every 1 month" / "Delivers every month" / "Deliver every month"
  //   "Deliver every 2 months" / "Delivers every 2 months"
  //   "Deliver every 15 days"  / "Delivers every 15 days"
  // Every one of them is a real, buyable plan allocated to that variant, so
  // neither the allocation rule nor the bulk/regular rule above touches them —
  // to the shopper they are simply the same choice three times, in a dropdown
  // she is using to decide how often a box arrives.
  //
  // Weeks are NOT folded into days here (a 2-week cadence stays 14 and a
  // 15-day cadence stays 15): they bill differently and Loop treats them as
  // different plans, so they are two offers that happen to sound alike, not
  // one offer said twice.
  var cadenceDays = function (label) {
    var text = (label || "").toLowerCase();
    var m = text.match(/every\s*(\d*)\s*(day|week|month|year)/);
    if (!m) return 0;
    // "every month" with no number is every ONE month.
    var n = m[1] === "" ? 1 : parseInt(m[1], 10);
    if (!n || n < 0) return 0;
    if (m[2] === "day") return n;
    if (m[2] === "week") return n * 7;
    if (m[2] === "month") return n * 30;
    return n * 365;
  };

  // Rows Loop emits that are not offers at all, plus the retired 6-week
  // cadence. NOTE: 15-day IS a live cadence and must not be listed here.
  var isMalformedOption = function (value, label) {
    var text = label || "";
    var lower = text.toLowerCase();
    if (
      text === "." ||
      text === "-" ||
      text.indexOf(". -") === 0 ||
      text.indexOf("- -") === 0
    ) {
      return true;
    }
    if (lower.indexOf("trial_and_") !== -1 || lower.indexOf("trial_and") === 0) {
      return true;
    }
    if (text.length < 3 && !isOneTimeOption(value, text)) return true;
    if (lower.indexOf("6 weeks") !== -1 || lower.indexOf("every 6 week") !== -1) {
      return true;
    }
    return false;
  };

  // 'onetime' | 'junk' | 'bulk' | 'regular' | 'unknown'.
  // 'unknown' is a first-class outcome, not a failure: it means "a real offer
  // this script doesn't recognise", and those are always kept.
  var classifyOption = function (value, label) {
    if (isOneTimeOption(value, label)) return "onetime";
    if (isMalformedOption(value, label)) return "junk";
    if (includes(HINT_BULK_PLAN_IDS, value) || looksBulk(label)) return "bulk";
    if (includes(HINT_REGULAR_PLAN_IDS, value) || looksRegular(label)) {
      return "regular";
    }
    return "unknown";
  };

  /**
   * Decide what to hide from one plan dropdown.
   *
   * options    [{ value, label, selected }] in DOM order
   * storedType 'bulk' | 'regular' | null — the shopper's last known choice from
   *            the Loop widget (legacy theme only; spark never sets it)
   * allowedIds [planId…] the plan ids this line's VARIANT is actually
   *            allocated to, or null when we haven't loaded them yet
   *
   * Returns { lineKind, remove: [value…], hasSubscriptionOption, bailed }.
   * `remove` is empty whenever hiding would orphan the current selection —
   * that's the guard that keeps the cart line intact.
   */
  var planFilter = function (options, storedType, allowedIds) {
    var opts = options || [];
    var kinds = [];
    var selected = null;
    var i;

    for (i = 0; i < opts.length; i += 1) {
      kinds.push(classifyOption(opts[i].value, opts[i].label));
      if (opts[i].selected) selected = i;
    }

    // The line's own plan is the truth about what kind of line this is. Fall
    // back to the remembered widget choice only when the selection tells us
    // nothing (one-time lines, or an offer we couldn't classify).
    var lineKind = null;
    if (selected !== null && (kinds[selected] === "bulk" || kinds[selected] === "regular")) {
      lineKind = kinds[selected];
    } else if (storedType === "bulk" || storedType === "regular") {
      lineKind = storedType;
    }

    // AMP builds this dropdown from `selling_plan_groups.map(g =>
    // g.selling_plans).flat()` — every plan on the PRODUCT, never the
    // allocations of the line's own variant. Loop publishes bulk cadences per
    // size, so a Small/Medium line lists all 16 (4 cadences x 4 sizes) and 12
    // of them cannot be bought on it. Drop anything not allocated to this
    // variant; skip the rule entirely until the allocations have loaded.
    var checkAllocations = !!(allowedIds && allowedIds.length);

    var remove = [];
    for (i = 0; i < opts.length; i += 1) {
      if (kinds[i] === "onetime") continue; // always buyable
      if (kinds[i] === "junk") {
        remove.push(opts[i].value);
        continue;
      }
      if (checkAllocations && !includes(allowedIds, opts[i].value)) {
        remove.push(opts[i].value);
        continue;
      }
      // Only ever hide a real offer because it belongs to the OTHER kind of
      // purchase. Unknown rows always survive.
      if (
        lineKind &&
        (kinds[i] === "bulk" || kinds[i] === "regular") &&
        kinds[i] !== lineKind
      ) {
        remove.push(opts[i].value);
      }
    }

    // ONE ROW PER RHYTHM. Everything above asks "may this line buy this plan?";
    // this asks "has the shopper already been offered this?". Loop's several
    // subscription groups each name the same cadence differently, so a monthly
    // box appeared three times over ("Deliver every 1 month", "Delivers every
    // month", "Deliver every month") — all real, all buyable, all identical to
    // the person reading them.
    //
    // The row the line is ALREADY on wins its rhythm, so a shopper never sees
    // her own choice disappear and a stranger's wording take its place; failing
    // that, the first in DOM order wins, which is the order Loop returns and
    // the one the PDP's own cadence list follows.
    var seenDays = {};
    if (selected !== null && kinds[selected] === "regular") {
      var ownDays = cadenceDays(opts[selected].label);
      if (ownDays) seenDays[ownDays] = true;
    }
    for (i = 0; i < opts.length; i += 1) {
      if (kinds[i] !== "regular") continue;
      if (i === selected) continue;
      if (includes(remove, opts[i].value)) continue; // already going
      var days = cadenceDays(opts[i].label);
      if (!days) continue; // no rhythm we can read — leave it alone
      if (seenDays[days]) remove.push(opts[i].value);
      else seenDays[days] = true;
    }

    // Refuse to hide the row the line is actually on. Without this the browser
    // resets the <select> to another plan, and AMP writes that plan to the cart.
    var bailed = false;
    if (selected !== null && includes(remove, opts[selected].value)) {
      remove = [];
      bailed = true;
    }

    var hasSubscriptionOption = false;
    for (i = 0; i < opts.length; i += 1) {
      if (kinds[i] === "onetime" || kinds[i] === "junk") continue;
      if (!includes(remove, opts[i].value)) hasSubscriptionOption = true;
    }

    return {
      lineKind: lineKind,
      remove: remove,
      hasSubscriptionOption: hasSubscriptionOption,
      bailed: bailed,
    };
  };
  // AMP-PLAN-FILTER-LOGIC-END

  /* ------------------------------------------------------------------
     DOM layer.
     ------------------------------------------------------------------ */
  var processedSelects = new WeakMap();
  var STORAGE_KEY_TYPE = "bcm_purchase_option_type";
  var STORAGE_KEY_PLAN_ID = "bcm_selected_plan_id";

  var readStorage = function (key) {
    try {
      return sessionStorage.getItem(key);
    } catch (err) {
      return null; // private mode / storage disabled
    }
  };
  var writeStorage = function (key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (err) {
      /* non-fatal */
    }
  };
  var clearStorage = function (key) {
    try {
      sessionStorage.removeItem(key);
    } catch (err) {
      /* non-fatal */
    }
  };

  // Remember what the shopper picked in Loop's PDP widget. LEGACY THEME ONLY —
  // the spark PDP renders its own chips and never defines these globals, which
  // is why planFilter() must not depend on this being set.
  var trackPurchaseOption = function () {
    var originalDelivery = window.changeInDeliveryOptionLoop;
    var originalGroup = window.changeInSellingPlanGroupLoop;

    if (typeof originalDelivery === "function") {
      window.changeInDeliveryOptionLoop = function (option) {
        originalDelivery(option);
        var planId = option.target.value;
        var productId = option.target.dataset.productId;
        if (planId && window.loopProps && window.loopProps[productId]) {
          writeStorage(STORAGE_KEY_PLAN_ID, planId);
          if (includes(HINT_BULK_PLAN_IDS, planId)) {
            writeStorage(STORAGE_KEY_TYPE, "bulk");
          } else if (includes(HINT_REGULAR_PLAN_IDS, planId)) {
            writeStorage(STORAGE_KEY_TYPE, "regular");
          }
        }
      };
    }

    if (typeof originalGroup === "function") {
      window.changeInSellingPlanGroupLoop = function (option) {
        originalGroup(option);
        var groupId = option.target.dataset.id;
        var groupName = (option.target.dataset.name || "").toLowerCase();
        var productId = option.target.dataset.productId;
        if (groupId === "loop-one-time-purchase") {
          writeStorage(STORAGE_KEY_TYPE, "regular");
          clearStorage(STORAGE_KEY_PLAN_ID);
        } else if (window.loopProps && window.loopProps[productId]) {
          var planId = window.loopProps[productId].sellingPlanId;
          if (planId) writeStorage(STORAGE_KEY_PLAN_ID, planId);
          writeStorage(
            STORAGE_KEY_TYPE,
            groupName.indexOf("bulk") !== -1 || groupName.indexOf("prepaid") !== -1
              ? "bulk"
              : "regular",
          );
        }
      };
    }
  };

  // Returns 'bulk' | 'regular' | null. null means "no idea" — and null is a
  // valid answer that planFilter() handles by leaving every offer visible.
  // The old code defaulted to 'regular' here, which on spark (where nothing
  // ever writes the key) meant every cart line was treated as a regular
  // subscription and every bulk option was stripped.
  var getStoredPurchaseType = function () {
    var type = readStorage(STORAGE_KEY_TYPE);
    if (type === "bulk" || type === "regular") return type;

    if (window.loopProps) {
      var ids = Object.keys(window.loopProps);
      for (var i = 0; i < ids.length; i += 1) {
        var state = window.loopProps[ids[i]];
        if (!state || !state.sellingPlanGroupId) continue;
        var name = (state.sellingPlanGroupName || "").toLowerCase();
        if (state.sellingPlanGroupId === "loop-one-time-purchase") {
          writeStorage(STORAGE_KEY_TYPE, "regular");
          return "regular";
        }
        if (name.indexOf("bulk") !== -1 || name.indexOf("prepaid") !== -1) {
          writeStorage(STORAGE_KEY_TYPE, "bulk");
          return "bulk";
        }
        if (name) {
          writeStorage(STORAGE_KEY_TYPE, "regular");
          return "regular";
        }
      }
    }
    return null;
  };

  /* ------------------------------------------------------------------
     Per-variant allocations. AMP puts everything we need in the DOM: the
     line root carries data-variant-id, and the select's id is
     `<handle>-subscription`. /products/<handle>.js is the same endpoint
     AMP itself fetches, so it is normally already in the HTTP cache.
     ------------------------------------------------------------------ */
  var productRequests = {}; // handle -> Promise, one flight per handle
  var allocations = {}; // handle -> { variantId: [planId…] }

  var loadAllocations = function (handle, onReady) {
    if (allocations[handle]) return allocations[handle];
    if (!productRequests[handle]) {
      productRequests[handle] = window
        .fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? window.Shopify.routes.root + "products/" + handle + ".js"
          : "/products/" + handle + ".js")
        .then(function (res) {
          return res.ok ? res.json() : null;
        })
        .then(function (product) {
          var byVariant = {};
          ((product && product.variants) || []).forEach(function (variant) {
            byVariant[String(variant.id)] = (
              variant.selling_plan_allocations || []
            ).map(function (allocation) {
              return String(allocation.selling_plan_id);
            });
          });
          allocations[handle] = byVariant;
          if (onReady) onReady();
        })
        .catch(function () {
          // Network/parse failure: leave allocations unset so planFilter keeps
          // every option. A cluttered dropdown beats a wrongly-pruned one.
          allocations[handle] = {};
        });
    }
    return null;
  };

  // "because-premium-overnight-plus-underwear-subscription" -> the handle
  var handleFromSelect = function (select) {
    var id = select.getAttribute("id") || "";
    var suffix = "-subscription";
    return id.slice(-suffix.length) === suffix
      ? id.slice(0, id.length - suffix.length)
      : "";
  };

  var setValidated = function (select, validated) {
    var container = select.closest(".upgrade-to-subscription");
    if (!container) return;
    if (validated) {
      container.classList.add("amp-subscription-validated");
    } else {
      container.classList.remove("amp-subscription-validated");
    }
  };

  var pruneSubscriptionOptions = function () {
    if (!document.getElementById("slidecarthq")) return;
    var storedType = getStoredPurchaseType();

    document.querySelectorAll("#slidecarthq select").forEach(function (select) {
      if (select.options.length < 1) return;

      var currentValue = select.value;

      // Plans this line's own variant can actually be bought on. Null while
      // the product is still loading — planFilter then skips the rule and we
      // re-run once it lands, so a slow request never prunes wrongly.
      var allowedIds = null;
      var handle = handleFromSelect(select);
      var line = select.closest(".item");
      var variantId = line && line.getAttribute("data-variant-id");
      if (handle && variantId) {
        var byVariant = loadAllocations(handle, pruneSubscriptionOptions);
        if (byVariant) allowedIds = byVariant[String(variantId)] || null;
      }

      // The memo has to include allocation-readiness, not just the option
      // list: the first pass often runs before /products/<handle>.js resolves
      // and removes nothing, and without this the re-run would early-return on
      // an unchanged option list and never apply the allocation rule.
      var signature =
        (allowedIds ? "alloc|" : "noalloc|") +
        Array.prototype.map
          .call(select.options, function (opt) {
            return opt.value + ":" + opt.textContent;
          })
          .join("|");

      var result = planFilter(
        Array.prototype.map.call(select.options, function (opt) {
          return {
            value: opt.value,
            label: (opt.textContent || "").trim(),
            selected: opt.value === currentValue,
          };
        }),
        storedType,
        allowedIds,
      );

      // Re-assert visibility on every pass, even for a select we've already
      // pruned — AMP toggles its own classes as the drawer re-renders.
      setValidated(select, result.hasSubscriptionOption);

      // Skip re-pruning a select whose option list hasn't changed since we last
      // saw it — AMP re-renders the drawer on every cart mutation.
      if (processedSelects.get(select) === signature) return;

      Array.prototype.slice.call(select.options).forEach(function (opt) {
        if (includes(result.remove, opt.value)) opt.remove();
      });

      // Removing options can reset selectedIndex, so put the shopper's own
      // choice back. planFilter guarantees it survived, so this restores a
      // value rather than picking a new one — and we never dispatch `change`:
      // the cart line is not ours to edit.
      if (currentValue !== select.value) {
        for (var i = 0; i < select.options.length; i += 1) {
          if (select.options[i].value === currentValue) {
            select.value = currentValue;
            break;
          }
        }
      }

      processedSelects.set(select, signature);
    });

    // Containers with no <select> at all are AMP's "upgrade to subscription"
    // prompt on a one-time line — always show those.
    document
      .querySelectorAll("#slidecarthq .upgrade-to-subscription")
      .forEach(function (container) {
        var select = container.querySelector("select");
        if (select && select.options.length > 0) return; // handled above
        if (container.querySelector("button.button")) {
          container.classList.add("amp-subscription-validated");
          container.style.display = "";
        }
      });
  };

  /* ------------------------------------------------------------------
     Wiring: AMP's own lifecycle hooks plus a debounced observer, because
     the drawer re-renders its markup on every cart mutation.
     ------------------------------------------------------------------ */
  var wrapAmpHook = function (name) {
    var existing = window[name];
    if (typeof existing === "function") {
      window[name] = function () {
        existing.apply(this, arguments);
        window.setTimeout(pruneSubscriptionOptions, 200);
      };
    } else {
      window[name] = function () {
        window.setTimeout(pruneSubscriptionOptions, 200);
      };
    }
  };
  wrapAmpHook("AMP_CART_OPENED");
  wrapAmpHook("AMP_CART_UPDATED");

  var debounce;
  var drawerObserver = new MutationObserver(function () {
    window.clearTimeout(debounce);
    debounce = window.setTimeout(pruneSubscriptionOptions, 150);
  });
  var observeDrawer = function (drawer) {
    drawerObserver.observe(drawer, { childList: true, subtree: true });
  };
  var drawer = document.getElementById("slidecarthq");
  if (drawer) {
    observeDrawer(drawer);
  } else {
    // The drawer is injected by the AMP app embed after first interaction.
    var waitForDrawer = new MutationObserver(function () {
      var el = document.getElementById("slidecarthq");
      if (!el) return;
      observeDrawer(el);
      waitForDrawer.disconnect();
    });
    waitForDrawer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  trackPurchaseOption();
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", pruneSubscriptionOptions);
  } else {
    pruneSubscriptionOptions();
  }
})();
